import { Library, UserCheck } from "lucide-react";

// Pricing Plans Configuration
// All pricing and copy in one place for easy updates

export const BILLING_PERIODS = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual'
};

export const pricingPlans = [
  {
    id: 'ce-membership',
    name: 'All Access Pass',
    icon: Library,
    tagline: 'Full library access. One simple fee.',
    pricing: {
      type: 'annual-only',
      amount: 129,
      label: '/yr',
      monthlyAmount: null,
      annualAmount: 129,
      annualSavings: null,
      stripePriceIdMonthly: null,
      stripePriceIdAnnual: 'price_ce_membership_annual'
    },
    bullets: [
      'Full access to all courses',
      'Certificates and CE tracking',
      'Access to Practice Resources',
      'New courses added regularly'
    ],
    primaryCta: {
      text: 'Get My All Access Pass Now',
      route: '/checkout?plan=ce-membership'
    },
    secondaryCta: {
      text: 'See details',
      route: '/pricing/ce-membership'
    },
    badge: null,
    badgeColor: '#102862',
    highlighted: true,
    color: '#FF6B35',
    // Detail page content
    detailPage: {
      heroSubtitle: 'Unlimited access to our complete course library with one membership.',
      whatYouGet: [
        'Unlimited access to all current courses',
        'Access to all new courses as they are added',
        'CE tracking dashboard',
        'Downloadable certificates for completed courses',
        'AI-powered study assistant',
        'Member-only updates and content',
        'Priority support'
      ],
      whoIsThisFor: [
        'Dentists who want ongoing CE education',
        'Practitioners building comprehensive oral surgery skills',
        'Those who prefer predictable monthly or annual pricing'
      ],
      howItWorks: [
        { step: 1, title: 'Subscribe', description: 'Choose monthly or annual billing and create your account.' },
        { step: 2, title: 'Access Everything', description: 'Immediately unlock the full course library.' },
        { step: 3, title: 'Learn & Earn CE', description: 'Complete courses at your pace and download certificates.' }
      ],
      faqs: [
        { question: 'Can I cancel anytime?', answer: 'Yes, you can cancel your membership at any time. You will retain access until the end of your billing period.' },
        { question: 'What happens to my certificates if I cancel?', answer: 'After you successfully complete each course, you will be sent an email with your completion certificate. You can download them and keep them permanently.' },
        { question: 'How often are new courses added?', answer: 'We add new courses regularly based on member feedback and emerging topics in oral surgery.' },
        { question: 'Do you offer team or practice pricing?', answer: 'Contact us for multi-seat licensing for dental practices and groups.' }
      ]
    }
  },
  {
    id: 'curbside-consult',
    name: 'Curbside Consult',
    icon: UserCheck,
    tagline: 'Direct surgeon feedback for your real-world cases.',
    pricing: {
      type: 'per-case',
      amount: 150,
      label: '/case',
      monthlyAmount: 150,
      annualAmount: 150,
      annualSavings: null,
      stripePriceIdMonthly: 'price_curbside_consult_per_case',
      stripePriceIdAnnual: 'price_curbside_consult_per_case'
    },
    bullets: [
      'Submit your case with images, CBCT, and clinical details',
      '1 follow-up question per case',
      '3-5 business day response time',
      'Text, video, audio, annotated images'
    ],
    primaryCta: {
      text: 'Explore Curbside Consult',
      route: '/checkout?plan=curbside-consult'
    },
    secondaryCta: {
      text: 'See details',
      route: '/pricing/curbside-consult'
    },
    badge: 'Expert Access',
    badgeColor: '#102862',
    highlighted: false,
    color: '#102862',
    // Detail page content
    detailPage: {
      heroSubtitle: 'Get direct feedback from a board-certified oral surgeon on your real cases.',
      whatYouGet: [
        'Submit your case with images, CBCT screenshots, and clinical details',
        '1 follow-up question per case',
        'Responses within 3-5 business days, often sooner depending on case backlog',
        'Response formats: text, short video, audio note, annotated images',
        'Priority support'
      ],
      whoIsThisFor: [
        'General dentists performing oral surgery procedures',
        'Practitioners who want expert guidance on complex cases',
        'Dentists building confidence with surgical extractions and implants'
      ],
      howItWorks: [
        { step: 1, title: 'Purchase a Case', description: 'Buy a Curbside Consult case for $150.' },
        { step: 2, title: 'Submit a Case', description: 'Upload images, CBCT screenshots, and describe your case.' },
        { step: 3, title: 'Get Expert Feedback', description: 'Receive detailed guidance within 3-5 business days.' },
        { step: 4, title: 'Follow Up', description: 'Ask one follow-up question per case for clarification.' }
      ],
      faqs: [
        { question: 'What counts as a "case question"?', answer: 'A case question is a clinical scenario you submit for feedback. It can include patient history, imaging, and your specific questions about treatment planning or technique.' },
        { question: 'What is included in the follow-up?', answer: 'Each case includes one follow-up question to clarify the initial response or ask for additional detail on the same case.' },
        { question: 'What is the response time?', answer: 'Responses are typically provided within 3-5 business days, often sooner depending on case backlog. Questions submitted on weekends or holidays are addressed the next business day.' },
        { question: 'Can I submit images or CBCT screenshots?', answer: 'Yes, you can upload clinical photos, radiographs, CBCT screenshots, and other relevant imaging with your case submission.' },
        { question: 'Is this for emergencies?', answer: 'No. Curbside Consult is for educational guidance and case planning, not emergency care. If you have a patient emergency, follow your standard emergency protocols.' }
      ],
      scopeDisclaimer: {
        title: 'Scope and Disclaimer',
        points: [
          'Curbside Consult provides educational guidance for licensed dental clinicians.',
          'This service is not intended for emergency care or time-sensitive clinical decisions.',
          'All final clinical decisions remain the responsibility of the treating provider.',
          'Responses are educational in nature and do not establish a doctor-patient relationship.'
        ]
      }
    }
  }
];

// Helper function to get plan by ID
export const getPlanById = (id) => pricingPlans.find(plan => plan.id === id);

// Helper function to format price
export const formatPrice = (amount, period = null) => {
  if (period === 'annual') {
    return `$${amount.toLocaleString()}/yr`;
  } else if (period === 'monthly') {
    return `$${amount}/mo`;
  }
  return `$${amount}`;
};
